<template>
  <header class="request-detail-header" data-testid="request-detail-header">
    <button
      type="button"
      class="request-detail-header__back"
      data-testid="request-detail-header__back-btn"
      @click="$emit('back')"
    >
      <span class="request-detail-header__back-icon" aria-hidden="true">
        <v-icon size="20">mdi-arrow-left</v-icon>
      </span>
      <span class="request-detail-header__back-text">Back to information request</span>
    </button>

    <div class="request-detail-header__title-row">
      <div class="request-detail-header__title-group">
        <div class="request-detail-header__title-line">
          <h1 class="request-detail-header__title" data-testid="request-detail-header__title">
            <span
              v-if="serialNumber != null"
              class="request-detail-header__ref"
              data-testid="request-detail-header__serial"
            >#{{ serialNumber }}</span>
            <span
              class="request-detail-header__description"
              :title="request.description || 'Information Request'"
            >
              {{ request.description || 'Information Request' }}
            </span>
          </h1>

          <div class="request-detail-header__meta">
            <StatusIndicator
              v-if="stageVariant"
              :variant="stageVariant"
              :custom-label="stageLabel"
              size="sm"
              outlined
              data-testid="request-detail-header__status"
            />

            <TaskDueDateCell
              v-if="request.duedate"
              :due-date="request.duedate"
              :due-days="request.duedate_days"
              days-only
              with-pill
              data-testid="request-detail-header__due-badge"
            />
          </div>
        </div>
      </div>

      <div class="request-detail-header__actions" data-testid="request-detail-header__actions">
        <v-btn
          v-for="action in labeledPrimaryActions"
          :key="action.id"
          small
          depressed
          :outlined="isOutlinedAction(action.id)"
          class="request-detail-header__action-btn"
          :class="actionButtonClass(action.id)"
          :data-testid="`request-detail-header__${action.id}`"
          @click="$emit('action', { actionId: action.id, request })"
        >
          <v-icon v-if="action.icon && !isAssetIcon(action.icon)" left small>
            {{ action.icon }}
          </v-icon>
          {{ action.label }}
        </v-btn>

        <button
          v-for="action in iconPrimaryActions"
          :key="action.id"
          type="button"
          class="request-detail-header__icon-btn"
          :aria-label="action.label"
          :data-testid="`request-detail-header__${action.id}`"
          @click="$emit('action', { actionId: action.id, request })"
        >
          <img
            v-if="isAssetIcon(action.icon)"
            :src="action.icon"
            alt=""
            class="request-detail-header__icon-btn-img"
            width="16"
            height="16"
          >
          <v-icon v-else small>{{ action.icon }}</v-icon>
        </button>

        <RequestRowOverflowMenu
          v-if="overflowActions.length"
          :actions="overflowActions"
          :request="request"
          :is-open="overflowOpen"
          @toggle="$emit('overflow-toggle', $event)"
          @action="$emit('action', $event)"
        />
      </div>
    </div>
  </header>
</template>

<script>
import './RequestDetailHeader.scss';
import StatusIndicator from '@/components/StatusIndicator.vue';
import { TaskDueDateCell } from '../../../TaskList/components';
import RequestRowOverflowMenu from '../RequestTable/RequestRowOverflowMenu.vue';
import { REQUEST_ACTION_IDS } from '../../utils/requestActions';
import {
  INFORMATION_MANAGER_STAGE_LABEL_MAP,
  INFORMATION_MANAGER_STAGE_STATUS_VARIANT,
} from '../../../../../utils/constants';
import { resolveStageLabel } from '../../../../../utils/informationManager';

export default {
  name: 'RequestDetailHeader',
  components: {
    StatusIndicator,
    TaskDueDateCell,
    RequestRowOverflowMenu,
  },
  props: {
    request: {
      type: Object,
      required: true,
    },
    serialNumber: {
      type: Number,
      default: null,
    },
    primaryActions: {
      type: Array,
      default: () => [],
    },
    overflowActions: {
      type: Array,
      default: () => [],
    },
    overflowOpen: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    stageLabel() {
      return resolveStageLabel(this.request?.stage, INFORMATION_MANAGER_STAGE_LABEL_MAP);
    },
    stageVariant() {
      const key = this.request?.stage;
      if (key === null || key === undefined) return null;
      return INFORMATION_MANAGER_STAGE_STATUS_VARIANT[key] || null;
    },
    labeledPrimaryActions() {
      return (this.primaryActions || []).filter((action) => !action.iconOnly);
    },
    iconPrimaryActions() {
      return (this.primaryActions || []).filter((action) => action.iconOnly);
    },
  },
  methods: {
    isAssetIcon(icon) {
      return typeof icon === 'string' && (icon.startsWith('/') || icon.startsWith('http'));
    },
    isOutlinedAction(actionId) {
      return actionId === REQUEST_ACTION_IDS.APPROVE || actionId === REQUEST_ACTION_IDS.REJECT;
    },
    actionButtonClass(actionId) {
      if (actionId === REQUEST_ACTION_IDS.APPROVE) return 'request-detail-header__action-btn--approve';
      if (actionId === REQUEST_ACTION_IDS.REJECT) return 'request-detail-header__action-btn--reject';
      return 'request-detail-header__action-btn--default';
    },
  },
};
</script>
